import { Injectable } from '@angular/core';
import { LoadingState } from '../../../common/enums/loading-state.enum';
import { DevicesHttpService } from '../../common/services/devices-http.service';
import { DeviceStatusPriority } from '../enums/device-status.dict';
import { getAreaIcon } from '../functions/area-utils';
import { IDashboard } from '../types/idashboard';
import { IDashboardAreaSummary } from '../types/idashboard-area-summary';
import { DashboardDeviceTile } from '../types/dashboard-device-tile';
import { DashboardCacheService } from './dashboard-cache.service';
import { DashboardDeviceService } from './dashboard-device.service';
import { DashboardSubscriptionService } from './dashboard-subscription.service';
import { Observable, forkJoin, of, throwError, BehaviorSubject, ReplaySubject } from 'rxjs';
import { DashboardTile } from '../types/dashboard-tile';
import { UserService } from 'src/app/modules/user/services/user.service';
import { ViewModelUpdateInfo } from '@caslink/common-client';
import { DashboardParentTile } from '../types/dashboard-parent-tile';
import { map, take, catchError, tap } from 'rxjs/operators';
import * as _ from 'lodash';

@Injectable({
    providedIn: 'root'
})
export class DashboardService {

    constructor(
        private dashboardCacheService: DashboardCacheService,
        private dashboardDeviceService: DashboardDeviceService,
        private dashboardSubscriptionService: DashboardSubscriptionService,
        private httpService: DevicesHttpService,
        private userService: UserService,
    ) {
    }

    private get dashboards() {
        return this.dashboardCacheService.dashboards;
    }

    private static bubbleDeviceStatusToParent(child: DashboardTile, parent: DashboardTile) {
        if (parent.deviceStatusType === undefined ||
            DeviceStatusPriority.get(child.deviceStatusType) > DeviceStatusPriority.get(parent.deviceStatusType)) {
                parent.deviceStatusType = child.deviceStatusType;
        }
    }

    public subscribeToDashboardUpdates() {
        this.dashboardSubscriptionService.subscribeToViewModelUpdates(this.updateViewModel);
    }

    public subscribeToDashboard(locationId: number) {
        const dashboard = this.dashboardCacheService.dashboards.get(locationId);
        const dashboardExists = !(!dashboard && !this.dashboardCacheService.locationsBeingFetched.has(locationId));

        if (!dashboardExists) {
            this.fetchDashboardData(this.initDashboard(locationId)).subscribe();
        }
        return of(this.dashboardCacheService.dashboards.get(locationId));
    }

    // Some action that needs to be done only once controls have been loaded for each device.
    public onControlsLoaded: (device: DashboardDeviceTile) => void = (_: DashboardDeviceTile) => null;

    private initDashboard(locationId: number) {
        const dashboard = { locationId, locationViewModel: { id: locationId } } as IDashboard;

        dashboard.areaDevices = new Map<number, DashboardDeviceTile[]>([]);
        dashboard.areaAlerts = new Map<number, number>([]);
        dashboard.deviceAlerts = new Map<number, number>([]);
        dashboard.startViewModelUpdates = new Map<number, Observable<void>>();
        dashboard.stopViewModelUpdates = new Map<number, Observable<void>>();
        dashboard.alertCount = 0;
        dashboard.areasById = new Map<number, IDashboardAreaSummary>();
        dashboard.areas = [];
        dashboard.$loading = new BehaviorSubject(true);
        dashboard.locationName = '';

        dashboard.$allLocationDataLoaded = new BehaviorSubject(false);
        dashboard.$loadedLocationData = new BehaviorSubject(false);
        dashboard.$signalR = new BehaviorSubject(false);

        this.dashboards.set(dashboard.locationId, dashboard);
        this.dashboardCacheService.locationsBeingFetched.add(dashboard.locationId);

        return this.dashboardCacheService.dashboards.get(dashboard.locationId);
    }

    public fetchDashboardData(dashboard: IDashboard) {
        return forkJoin(
            [
                this.getDashboardAreas(dashboard.locationId),
                this.userService.getLocation(dashboard.locationId),
            ],
        )
        .pipe(
            take(1),
            catchError(error => {
                this.afterDashboardFetch(dashboard.locationId);
                return throwError(error);
            }),
            map(([loadedAreas, userLocation]) => {
                dashboard.locationName = userLocation.description;
                dashboard.locationViewModel = userLocation;
                dashboard.areas = loadedAreas.areas;
                return dashboard;
            }),
            tap(() => {
                this.afterDashboardFetch(dashboard.locationId);
            }),
        )
    }

    private afterDashboardFetch(dashboardLocationId: number) {
        const cachedDashboard = this.dashboardCacheService.dashboards.get(dashboardLocationId);
        this.setDashboardAreas(cachedDashboard);
        cachedDashboard.$loadedLocationData.next(true);
    }

    private updateViewModel = (data: ViewModelUpdateInfo) => {
        const newViewModel = data.viewModel;
        const dashboards = Array.from(this.dashboardCacheService.dashboards.values());
        if (dashboards) {
            dashboards.forEach(dashboard => {
                if (dashboard.areas) {
                    dashboard.areas.forEach((area) => {
                        const devices = dashboard.areaDevices.get(area.areaId);
                        if (devices) {
                            devices.forEach(d => {
                                if (d.controls &&
                                    d.controls.controlSummaryViewModel &&
                                    d.controls.controlSummaryViewModel.identifier.entityId === data.entityId
                                ) {
                                    this.updateDeviceViewModel(d, area, dashboard, newViewModel);
                                    return;
                                }
                            });
                        }
                    });
                }
            });
        }
    }

    private updateDeviceViewModel = (
        currentDevice: DashboardDeviceTile,
        currentArea: IDashboardAreaSummary,
        currentLocation: IDashboard,
        newViewModel: any
    ) => {
        this.resetAllSensorCountsForParent(currentArea, currentDevice.hardwareItem.id);
        this.resetAllSensorCountsForParent(currentLocation, currentDevice.hardwareItem.id);

        currentDevice.controls.controlSummaryViewModel = newViewModel;

        this.dashboardSubscriptionService.cacheGetControlsForDeviceResponse(currentDevice.hardwareItem.id, {
            type: currentDevice.hardwareCategory,
            control: currentDevice.controls
        });

        this.dashboardDeviceService.setupControlsForDevice(currentLocation, currentDevice, currentArea);

        currentDevice.$signalR.next(true);
        currentArea.$signalR.next(true);
        currentLocation.$signalR.next(true);
    }

    private resetAllSensorCountsForParent = (parent: DashboardParentTile, deviceId: number) => {
        const props: (
            keyof DashboardParentTile &
            ('lights' | 'roomLights' | 'hoodLights' | 'fans' | 'roomFans' | 'hoodFans' | 'occupancy')
        )[] = [
            'lights',
            'roomLights',
            'hoodLights',
            'fans',
            'roomFans',
            'hoodFans',
            'occupancy'
        ];

        props.forEach((prop) => {
            if (parent[prop]) {
                const onSensors = parent[prop].sensor.onSensorsByDevice.get(deviceId);
                if (onSensors) {
                    parent[prop].sensor.onSensorCount -= onSensors;
                    parent[prop].sensor.onSensorsByDevice.set(deviceId, 0);
                    // Ensure the area/location is turned off if there are no longer any "on" children
                    if (parent[prop].sensor.onSensorCount === 0 && parent[prop].sensor.on) {
                        parent[prop].sensor.on = false;
                    }
                }
                const totalSensors = parent[prop].sensor.totalSensorsByDevice.get(deviceId);
                if (totalSensors) {
                    parent[prop].sensor.totalSensors -= totalSensors;
                    parent[prop].sensor.totalSensorsByDevice.set(deviceId, 0);
                }
            }
        });
    }

    public getDashboardAreas(locationId: string | number): Observable<IDashboard> {
        return this.httpService.get<IDashboard>(`dashboard/getDevices/${locationId}`);
    }

    public getControlsForDevice(id: number | string) {
        return this.httpService.get<any>(`dashboard/getControlsForDevice/${id}`);
    }

    private setDashboardAreas(dashboard: IDashboard) {
        dashboard.areas = dashboard.areas.filter(area => {
            if (area.areaTag || (area.devices && area.devices.length > 0)) {
                dashboard.areasById.set(area.areaId, area);
                return true;
            }

            return false;
        });
        dashboard.hardwareItems = new Map<number, string>();

        // The dashboard is done loading if it has no areas
        if (!dashboard.areas.length) {
            dashboard.$loading.next(false);
            dashboard.$loadedLocationData.next(true);
            dashboard.$allLocationDataLoaded.next(true);
            return;
        }

        dashboard.areas.forEach(area => {
            area.$signalR = new BehaviorSubject(false);
            area.$loading = new BehaviorSubject(true);
        });

        dashboard.areas.forEach(area => {
            if (area.areaId === undefined) {
                area.areaId = -1;
            }
            let devices: DashboardDeviceTile[];
            area.hardwareItems = new Map<number, string>();

            const areaIcon = getAreaIcon(area);
            if (areaIcon) {
                area.thumbnail = { icon: areaIcon, isSvg: true };
            }

            devices = (area.devices as DashboardDeviceTile[]);
            dashboard.areaDevices.set(area.areaId, devices);
            dashboard.areaAlerts.set(area.areaId, 0);

            // The area is done loading if it has no devices
            if (!area.devices || area.devices.length === 0) {
                this.indicateAreaLoaded(dashboard, area);
            }

            devices.forEach(device => {
                device.$loading = new BehaviorSubject(true);
                device.$signalR = new BehaviorSubject(false);
            })

            devices.forEach(device => {
                this.getAndSetupControlsForDevice(dashboard, device, area);
                DashboardService.bubbleDeviceStatusToParent(device, area);
            });

            DashboardService.bubbleDeviceStatusToParent(area, dashboard);
        });
    }

    private indicateLocationLoaded(dashboard: IDashboard) {
        dashboard.$loading.next(false);
        dashboard.$allLocationDataLoaded.next(true);
    }

    private indicateAreaLoaded(dashboard: IDashboard, area: IDashboardAreaSummary) {
        area.$loading.next(false);

        const isLocationLoading = dashboard.$loading.getValue();
        const locationAreasLoading = this.isLoading(dashboard.areas);

        if (isLocationLoading && !locationAreasLoading) {
            this.indicateLocationLoaded(dashboard);
        }
    }

    private indicateDeviceLoaded(dashboard: IDashboard, device: DashboardDeviceTile, area: IDashboardAreaSummary) {
        const isDeviceLoading = device.$loading.getValue();

        if (isDeviceLoading) {
            device.$loading.next(false);

            const devicesOfArea = dashboard.areaDevices.get(area.areaId);
            const isAreaLoading = area.$loading.getValue();

            if (isAreaLoading && !this.isLoading(devicesOfArea)) {
                this.indicateAreaLoaded(dashboard, area);
            }
        }
    }

    private isLoading(tile: DashboardDeviceTile[] | IDashboardAreaSummary[]) {
        return tile.some(tile => tile.$loading.getValue());
    }

    private getAndSetupControlsForDevice(dashboard: IDashboard, device: DashboardDeviceTile, area: IDashboardAreaSummary) {

        this.getControlsForDevice(device.hardwareItem.id)
            .pipe(
                take(1),
            )
            .subscribe(response => {
                if (response) {
                    device.hardwareCategory = response.type;
                    device.controls = response.control;
                    this.dashboardDeviceService.setupControlsForDevice(dashboard, device, area);
                    device.controlsLoadingState = LoadingState.Ready;
                }
            },
            () => {
                device.controlsLoadingState = LoadingState.Error;
            },
            () => {
                this.indicateDeviceLoaded(dashboard, device, area);
            });
    }
}
